import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { Truck, ShieldCheck, HeadphonesIcon } from 'lucide-react';
import { useNavigation } from '../context/NavigationContext';
import { Screen } from '../types';
import { Button } from '../components/ui/Button';
export function WelcomeScreen() {
  const {
    navigate,
    resetTo
  } = useNavigation();
  const floatingAnimation = {
    y: [0, -10, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut'
    }
  };
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.4
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      x: -20
    },
    show: {
      opacity: 1,
      x: 0,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 24
      }
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} className="flex flex-col min-h-screen bg-gradient-to-b from-primary-800 to-primary overflow-hidden relative" data-id="element-2686">
      {/* Subtle Decorative Elements */}
      <motion.div animate={floatingAnimation} className="absolute top-12 left-8 text-4xl opacity-15 select-none pointer-events-none" data-id="element-2687">
        🛍️
      </motion.div>
      <motion.div animate={{
      ...floatingAnimation,
      transition: {
        ...floatingAnimation.transition,
        delay: 1,
        duration: 5
      }
    }} className="absolute top-24 right-10 text-5xl opacity-15 select-none pointer-events-none" data-id="element-2688">
        🎁
      </motion.div>
      <motion.div animate={{
      ...floatingAnimation,
      transition: {
        ...floatingAnimation.transition,
        delay: 2,
        duration: 4.5
      }
    }} className="absolute top-1/2 left-12 text-3xl opacity-20 select-none pointer-events-none text-accent-200" data-id="element-2689">
        ⭐
      </motion.div>

      {/* Top Content Section */}
      <div className="flex-1 flex flex-col items-center justify-center px-8 pt-20 pb-8 z-10" data-id="element-2690">
        {/* Logo & Tagline */}
        <motion.div initial={{
        scale: 0.9,
        opacity: 0,
        y: 20
      }} animate={{
        scale: 1,
        opacity: 1,
        y: 0
      }} transition={{
        type: 'spring',
        stiffness: 200,
        damping: 20
      }} className="flex flex-col items-center mb-12 w-full max-w-[280px]" data-id="element-2691">
          <img src="https://cdn.magicpatterns.com/uploads/qwjBm9dydTfJXkZFWZ9RtX/JUMMALL_UPDATED_LOGO_(1).png" alt="JUMMALL Logo" className="w-48 h-auto object-contain drop-shadow-lg mb-6" data-id="element-2692" />
          <p className="text-white/90 text-sm font-medium tracking-wide text-center" data-id="element-2693">
            ...one store, endless possibilities
          </p>
        </motion.div>

        {/* Feature Pills - Vertical Stack */}
        <motion.div variants={containerVariants} initial="hidden" animate="show" className="flex flex-col gap-3 w-full max-w-[260px]" data-id="element-2694">
          <motion.div variants={itemVariants} className="ml-0" data-id="element-2695">
            <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm" data-id="element-2696">
              <Truck className="w-5 h-5 text-white" data-id="element-2697" />
              <span className="text-sm font-medium text-white" data-id="element-2698">
                Free Shipping
              </span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="ml-6" data-id="element-2699">
            <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm" data-id="element-2700">
              <ShieldCheck className="w-5 h-5 text-white" data-id="element-2701" />
              <span className="text-sm font-medium text-white" data-id="element-2702">
                Secure Payments
              </span>
            </div>
          </motion.div>

          <motion.div variants={itemVariants} className="ml-12" data-id="element-2703">
            <div className="flex items-center gap-3 px-5 py-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 shadow-sm" data-id="element-2704">
              <HeadphonesIcon className="w-5 h-5 text-white" data-id="element-2705" />
              <span className="text-sm font-medium text-white" data-id="element-2706">
                24/7 Support
              </span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Smooth Curved Wave Divider */}
      <div className="w-full overflow-hidden leading-none -mb-1 z-10 relative" data-id="element-2707">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 120" preserveAspectRatio="none" className="w-full h-16 sm:h-20 fill-white" data-id="element-2708">
          <path d="M0,64L80,69.3C160,75,320,85,480,80C640,75,800,53,960,48C1120,43,1280,53,1360,58.7L1440,64L1440,120L1360,120C1280,120,1120,120,960,120C800,120,640,120,480,120C320,120,160,120,80,120L0,120Z" data-id="element-2709"></path>
        </svg>
      </div>

      {/* Bottom Action Section */}
      <motion.div initial={{
      opacity: 0,
      y: 40
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.6,
      type: 'spring',
      stiffness: 200,
      damping: 24
    }} className="px-6 pt-6 pb-12 flex flex-col gap-4 bg-white z-10 relative" data-id="element-2710">
        <Button fullWidth size="lg" onClick={() => navigate(Screen.LOGIN)} className="rounded-2xl text-lg font-bold shadow-sm" data-id="element-2711">
          Login
        </Button>

        <Button fullWidth size="lg" variant="outline" onClick={() => navigate(Screen.SIGNUP)} className="border-gray-200 rounded-2xl text-lg font-bold text-gray-800 hover:bg-gray-50" data-id="element-2712">
          Sign Up
        </Button>

        <div className="mt-4 text-center" data-id="element-2713">
          <button onClick={() => resetTo(Screen.HOME)} className="text-sm font-bold text-gray-500 hover:text-primary transition-colors underline underline-offset-4 decoration-gray-300 hover:decoration-primary" data-id="element-2714">
            Continue as Guest
          </button>
        </div>
      </motion.div>
    </motion.div>;
}